class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement
    // when window.StandardEvents is available. Its `view-event-trigger="manual"`
    // skips auto-dispatch on connect; we fire it here when the drawer opens, with
    // `context: 'dialog'` from the payload attribute.
    //
    // The call is optional, not just the lookup: when CartItems falls back to
    // HTMLElement the element exists but the factory's dispatchViewEvent does
    // not, so `?.dispatchViewEvent()` would throw on every drawer open. This
    // matches quick-add.js, which already invokes it this way.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close() {
    // A pending focus restore belongs to an open drawer; closing cancels it so a later add from
    // anywhere cannot pull focus back into a hidden (and possibly closing) drawer.
    this.focusRestorePending = false;
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const hadFocusInDrawer = this.contains(document.activeElement);
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      if (hadFocusInDrawer) this.focusRestorePending = true;
    });
  }

  /*
   * The swap above removes whatever was focused, and `open()` no-ops while the drawer is already
   * active, so a keyboard add would drop focus to <body>. The add-on add replaces the drawer
   * twice: `renderContents` swaps `#CartDrawer`, then `cart-drawer-items` re-renders
   * `.cart-drawer__footer` from the cartUpdate it publishes -- a network round trip later.
   * Focusing in between would only be undone, so `CartItems.onCartUpdate` calls this once the
   * second swap has landed. Targets the first add-on control, or the close button when the row is
   * gone (e.g. the cart now meets the free-shipping threshold). Re-trap because the drawer's
   * contents were replaced.
   */
  restoreFocus() {
    // The refetch may return after the shopper closed the drawer. Moving focus -- and the trap
    // that follows -- into a drawer that is no longer active would be wrong.
    if (!this.focusRestorePending || !this.classList.contains('active')) return;
    this.focusRestorePending = false;

    const target = this.querySelector('.cart-drawer__addon-add') || this.querySelector('.drawer__close');
    if (!target) return;
    target.focus();
    trapFocus(this.querySelector('.drawer__inner'), target);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return HTMLUpdateUtility.getSectionInnerHTML(html, selector);
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
